import IOLazyLoad from '@bva/iolazyload';

const dom = {
  id: 'data-timer-id',
  timer: 'data-timer',
  message: 'data-complete-message',
  format: 'data-timer-format',
  lazy: 'data-lazy-timer',
  pause: 'data-pause-timer',
};

const state = {};

/**
 * A timer has ended. so must all things.
 *
 * @param: {Interval} timer: the interval for a timer element
 * @param: {Node} timerElement: the countdown/timer element that has reached the end of its countdown
 */
const endTimer = (timerElement) => {
  pauseTimer(timerElement);
  // fire event notifying the timer has ended
  const timerEvent = new Event('countdownTimerEnded');
  timerElement.dispatchEvent(timerEvent);
};

/**
 * Adds a leading 0 to a number if the number is less than 10
 *
 * @param (int) number: the number to format
 *
 * @return: (String): the string representation of the number
 */
const withLeadingZero = (number) => {
  return number < 10 ? `0${number}` : number;
};

/**
 * Builds the formatted representation of a time
 *
 * @param: {Node} timerElement: element that is the timer container
 * @param: {int} days: the days left on the countdown
 * @hours: {int} hours: the hours left on the countdown
 * @minutes: {int} minutes: the minutes left on the countdown
 * @seconds: {int} seconds: the seconds left on the countdown
 *
 * @return: {String} the formatted string representation of the time passed
 */
const getTimerOutput = (timerElement, days, hours, minutes, seconds) => {
  let formattedTime = timerElement.hasAttribute(dom.format) ? timerElement.getAttribute(dom.format) : '%D:%H:%M:%S';
  formattedTime = formattedTime.replace(/%D/g, withLeadingZero(days));
  formattedTime = formattedTime.replace(/%d/g, days);
  formattedTime = formattedTime.replace(/%H/g, withLeadingZero(hours));
  formattedTime = formattedTime.replace(/%h/g, hours);
  formattedTime = formattedTime.replace(/%M/g, withLeadingZero(minutes));
  formattedTime = formattedTime.replace(/%m/g, minutes);
  formattedTime = formattedTime.replace(/%S/g, withLeadingZero(seconds));
  formattedTime = formattedTime.replace(/%s/g, seconds);
  return formattedTime;
};

/**
 * Returns a string representation of a passed time in seconds
 *
 * @param: {int} time: time in seconds to display
 * @param: {Node} timerElement: element that is the timer container
 *
 * @return: {String} the string representation of the time passed
 */
const timeToString = (time, timerElement) => {
  // timer needs a minimum value - don't want to go below 0
  if (time < 0) {
    const hasFinishedMessage = timerElement.hasAttribute(dom.message);
    if (hasFinishedMessage) {
      return timerElement.getAttribute(dom.message);
    } else {
      return getTimerOutput(timerElement, 0, 0, 0, 0);
    }
  }
  // have a positive time on the clock - need to get different time increments
  const seconds = Math.floor(time % 60);
  const minutes = Math.floor((time / 60) % 60);
  const hours = Math.floor((time / 3600) % 24);
  const days = Math.floor(time / 86400);
  return getTimerOutput(timerElement, days, hours, minutes, seconds);
};

/**
 * Set the countdown elements text to reflect the new time left
 *
 * @param: {Node} timerElement: element that is the timer container
 */
const setNewTime = (timerElement) => {
  // display time
  const rawTime = timerElement.getAttribute(dom.timer);
  const displayTime = timeToString(rawTime, timerElement);
  timerElement.innerHTML = displayTime;
  // set new time attribute for time remaining
  if (rawTime < 0) {
    endTimer(timerElement);
  } else {
    timerElement.setAttribute(dom.timer, rawTime - 1);
  }
};

/**
 * Add a timer ID to a timer element for tracking its interval while active
 *
 * @param: {Node} timerElement: the element to add the ID to
 *
 * @return: {String}: the new ID associated with the timerElement
 */
const addTimerID = (timerElement) => {
  let newID = false;
  // need to ensure that the ID is unique, so create IDs until we have one that isn't being used
  while (!newID || document.querySelectorAll(`[${dom.id}="${newID}"]`).length > 0) {
    newID = Date.now() - Math.floor(Math.random() * 1000000);
  }
  timerElement.setAttribute(dom.id, newID);
  return newID;
}

/**
 * Gets the ID associated with the timer element passed
 *
 * @param: {Node} timerElement: element that is the timer container
 *
 * @return: {String}: id of the countdown element
 */
const getTimerID = (timerElement) => {
  return timerElement.hasAttribute(dom.id) ? timerElement.getAttribute(dom.id) : addTimerID(timerElement);
};

/**
 * Pauses the interval for a countdown
 *
 * @param: {Node} timerElement: element that is the timer container
 */
const pauseTimer = (timerElement) => {
  const timerID = getTimerID(timerElement);
  const timerInterval = state[timerID];
  clearInterval(timerInterval);
  state[timerID] = null;
};

/**
 * Create a new active timer for an element if it doesn't exist
 *
 * @param: {Node} timerElement: element that is the timer container
 */
const startTimer = (timerElement) => {
  // ensure the timer isn't already started
  if (haveTimer(timerElement)) {
    return;
  }
  // set the initial timer text
  setNewTime(timerElement);
  // create timer interval for tracking the countdown
  let timer = setInterval(() => {
    setNewTime(timerElement);
  }, 1000);
  // add new interval to the state
  const timerID = getTimerID(timerElement);
  state[timerID] = timer;
};

/**
 * Check if a countdown instance has an active timer
 *
 * @param: {Node} timerElement: element that is the timer container
 *
 * @return: {boolean}: whether there is an associated timer with the countdown timer element
 */
const haveTimer = (timerElement) => {
  const timerID = getTimerID(timerElement);
  return state[timerID];
};

/**
 * Check if a countdown instance has time left in the countdown or if it's finished
 *
 * @param: {Node} timerElement: element that is the timer container
 *
 * @return: {boolean}: if the timer has time left
 */
const timerFinished = (timerElement) => {
  const rawTimeLeft = timerElement.hasAttribute(dom.timer) ? timerElement.getAttribute(dom.timer) : -1;
  const timeLeft = parseInt(rawTimeLeft, 10);
  return timeLeft < 0;
};

/**
 * Check if a countdown instance has an active timer
 *
 * @param: {Node} entry: a single countdown element that has entered/exited the viewport
 * @param: {Object} observer: the observer object that watches & notifies countdown elements
 * @param: {boolean} isIntersecting: whether the entry passed is in the viewport
 */
const checkStartTimer = (entry, observer, isIntersecting) => {
  if (isIntersecting && !haveTimer(entry) && !timerFinished(entry)) {
    startTimer(entry);
  } else if (!isIntersecting) {
    // only pause the timer if the element has the pause data attribute
    entry.hasAttribute(dom.pause) && pauseTimer(entry);
  }
  // can unobserve the countdown
  if (timerFinished(entry)) {
    observer.unobserve(entry);
  }
};

/**
 * Setup for all timers
 *
 * Takes an object with the following settings:
 *
 * @param: {boolean} loadInView: whether the timers should wait to be started till they are in the viewport. Defaults to true
 * @param: {boolean} pauseOnLeave: whether the timers should pause when the timer element leaves the viewport. Defaults to true
 * @param: {Function} errorCallback: the callback for if Intersection Observers aren't supported by the browser.
 */
const setup = ({ errorCallback=startTimer }) => {
  // load all the non-lazy timers
  const nonLazyLoaded = document.querySelectorAll(`[${dom.timer}]:not([${dom.lazy}])`);
  nonLazyLoaded.forEach((element) => startTimer(element));
  // setup listeners for all the lazy loaded timers
  IOLazyLoad.setupObservers({
    queryString: `[${dom.timer}][${dom.lazy}]`,
    callback: checkStartTimer,
    errorCallback,
  });
};

export default {
  setup,
  startTimer,
  pauseTimer,
};
